import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Copy, Download, ExternalLink, ShieldAlert, Check, ChevronLeft, ArrowRight,
  SkipForward, Flag, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import OpsTopBar from "../../components/ops/OpsTopBar";
import { BranchAvatar } from "../../components/ops/BranchAvatar";
import { StatusPill, PriorityPill, MethodBadge, PlatformIcon, FormatTag } from "../../components/ops/Pills";
import { METHOD_META } from "../../lib/ops";
import { useOpsRole } from "../../context/OpsRoleContext";
import useApi from "../../hooks/useApi";
import api, { apiError } from "../../lib/api";

function copy(text, label) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

export default function OpsTask() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useOpsRole();
  const { data, refetch } = useApi(`/ops/tasks/${id}?role=${role}`, [id, role]);
  const [checked, setChecked] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const task = data?.task;
  const branch = data?.branch;
  if (!task) return (<><OpsTopBar title="Task" /><div className="p-8 text-ink-2">Loading task…</div></>);

  const steps = task.checklist || [];
  const allDone = steps.length > 0 && steps.every((s) => checked[s.id]);
  const igAccount = (branch?.accounts || []).find((a) => a.platform === task.platform);

  const goNext = () => {
    if (data?.next_task_id) navigate(`/ops/task/${data.next_task_id}`);
    else navigate("/ops");
  };

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/ops/tasks/${task.id}/publish`);
      toast.success("Published — logged to branch history");
      setConfirming(false);
      goNext();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not publish");
    } finally { setBusy(false); }
  };

  const skip = async () => { await api.post(`/ops/tasks/${task.id}/skip`); toast("Task skipped"); goNext(); };
  const flag = async () => { await api.post(`/ops/tasks/${task.id}/flag`, { note: "Operator flagged for review" }); toast("Flagged to Requires Attention"); refetch(); };

  const done = task.status === "completed";

  return (
    <>
      <OpsTopBar title="Publishing workspace" subtitle={`${task.branch_name} · ${task.group_name}`} />
      <div className="p-6 sm:p-8 max-w-[1400px]">
        <Link to="/ops/queue" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink mb-4" data-testid="task-back"><ChevronLeft size={15} /> Back to queue</Link>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* LEFT — content preview */}
          <div className="card overflow-hidden" data-testid="task-preview">
            <div className="relative">
              <img src={task.property?.image} alt="" className="w-full h-72 object-cover" />
              <div className="absolute top-3 left-3 flex gap-2">
                <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur" style={{ background: "rgba(0,0,0,.55)" }}>{task.format}</span>
                <span className="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur inline-flex items-center gap-1.5" style={{ background: "rgba(0,0,0,.55)" }}><PlatformIcon platform={task.platform} size={12} /> {task.platform}</span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-bold tracking-tight text-ink">{task.property?.address}</div>
                  <div className="text-sm text-ink-2">{task.property?.type} · <span className="font-semibold text-ink">{task.property?.price}</span> · {task.property?.status}</div>
                </div>
                <span className="rounded-full px-3 py-1.5 text-xs font-bold tracking-wide" style={{ background: "var(--grad-green-soft)", color: "#5F8213" }}>{task.cta}</span>
              </div>
              <p className="text-sm text-ink mt-4 leading-relaxed rounded-xl p-3.5" style={{ background: "#F6F8F1" }} data-testid="task-caption">{task.caption}</p>
              <div className="flex items-center gap-2 mt-3 text-xs text-ink-2 rounded-lg border px-3 py-2 break-all" style={{ borderColor: "#E7E8E4" }}>
                <ExternalLink size={13} /> {task.url}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                <button onClick={() => copy(task.caption, "Caption")} className="btn-secondary focus-ring justify-center text-xs" data-testid="copy-caption"><Copy size={13} /> Caption</button>
                <button onClick={() => copy(task.url, "Property link")} className="btn-secondary focus-ring justify-center text-xs" data-testid="copy-link"><Copy size={13} /> Link</button>
                <a href={task.property?.image} target="_blank" rel="noreferrer" className="btn-secondary focus-ring justify-center text-xs" data-testid="download-creative"><Download size={13} /> Creative</a>
                <a href={task.url} target="_blank" rel="noreferrer" className="btn-secondary focus-ring justify-center text-xs" data-testid="open-property"><ExternalLink size={13} /> Property</a>
              </div>
            </div>
          </div>

          {/* RIGHT — publish checklist */}
          <div className="space-y-4">
            {/* Safety banner */}
            <div className="card p-5 relative overflow-hidden" style={{ borderColor: task.branch_color, borderWidth: 2 }} data-testid="branch-safety-banner">
              <div className="flex items-center gap-4">
                <BranchAvatar initials={task.branch_initials} color={task.branch_color} size={52} />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-3"><ShieldAlert size={13} /> You are publishing for</div>
                  <div className="text-lg font-extrabold tracking-tight text-ink truncate">{task.group_name} — {task.branch_name}</div>
                  <div className="text-sm flex items-center gap-1.5" style={{ color: task.branch_color }}><PlatformIcon platform={task.platform} size={13} /> {igAccount?.handle || task.handle} · {igAccount?.method || "Manual Publishing"}</div>
                </div>
              </div>
            </div>

            <div className="card p-5">
              <div className="flex items-center justify-between mb-1">
                <span className="label-caps">Publishing method</span>
                <MethodBadge method={task.method} />
              </div>
              <p className="text-sm text-ink-2">{METHOD_META[task.method]?.desc}</p>
            </div>

            {/* Checklist */}
            <div className="card p-5" data-testid="publish-checklist">
              <div className="flex items-center justify-between mb-3">
                <span className="label-caps">Publishing checklist</span>
                <div className="flex items-center gap-2"><PriorityPill priority={task.priority} /><StatusPill status={task.status} /></div>
              </div>
              <div className="space-y-1">
                {steps.map((s, i) => (
                  <button key={s.id} disabled={done} onClick={() => setChecked((c) => ({ ...c, [s.id]: !c[s.id] }))}
                    data-testid={`step-${i}`} className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#F6F8F1] disabled:opacity-60">
                    <span className="grid place-items-center w-5 h-5 rounded-md border shrink-0 transition-colors"
                      style={checked[s.id] || done ? { background: "var(--grad-green)", borderColor: "transparent" } : { borderColor: "#CFD2C6" }}>
                      {(checked[s.id] || done) && <Check size={13} strokeWidth={3} color="#16180F" />}
                    </span>
                    <span className={`text-sm ${checked[s.id] || done ? "text-ink-3 line-through" : "text-ink"}`}>{s.label}</span>
                  </button>
                ))}
              </div>

              {done ? (
                <div className="mt-4 rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2" style={{ background: "var(--grad-green-soft)", color: "#5F8213" }}>
                  <Check size={15} strokeWidth={2.5} /> Published successfully
                </div>
              ) : (
                <>
                  <button disabled={!allDone} onClick={() => setConfirming(true)} data-testid="mark-published"
                    className="btn-primary w-full justify-center focus-ring mt-4 !py-3 disabled:opacity-40">
                    Mark as Published
                  </button>
                  {!allDone && <p className="text-center text-xs text-ink-3 mt-2">Complete every step to enable publishing.</p>}
                </>
              )}

              <div className="flex gap-2 mt-3">
                <button onClick={skip} className="btn-secondary flex-1 justify-center focus-ring text-sm" data-testid="task-skip"><SkipForward size={14} /> Skip</button>
                <button onClick={flag} className="btn-secondary flex-1 justify-center focus-ring text-sm" data-testid="task-flag"><Flag size={14} /> Flag issue</button>
                {data?.next_task_id && <button onClick={goNext} className="btn-secondary flex-1 justify-center focus-ring text-sm" data-testid="task-next">Next <ArrowRight size={14} /></button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirming && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => !busy && setConfirming(false)} data-testid="publish-confirm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <BranchAvatar initials={task.branch_initials} color={task.branch_color} size={44} />
              <div><div className="font-bold text-ink">Confirm publication</div><div className="text-xs text-ink-2">This will log to the branch audit trail.</div></div>
            </div>
            <dl className="space-y-2 text-sm rounded-xl p-4" style={{ background: "#F6F8F1" }}>
              <div className="flex justify-between"><dt className="text-ink-2">Branch</dt><dd className="font-semibold text-ink">{task.group_name} — {task.branch_name}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-2">Account</dt><dd className="font-semibold text-ink">{igAccount?.handle || task.handle}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-2">Content</dt><dd className="font-semibold text-ink">{task.property?.address}</dd></div>
              <div className="flex justify-between"><dt className="text-ink-2">Format</dt><dd className="font-semibold text-ink">{task.platform} {task.format}</dd></div>
            </dl>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setConfirming(false)} disabled={busy} className="btn-secondary flex-1 justify-center focus-ring" data-testid="confirm-cancel">Cancel</button>
              <button onClick={publish} disabled={busy} className="btn-primary flex-1 justify-center focus-ring" data-testid="confirm-publish">
                {busy ? <Loader2 size={15} className="animate-spin" /> : "Confirm Published"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
